import { equals, flatten, intersperse, is } from 'ramda';
import React from 'react';

export const defaultComponents = {
  br: { needle: '[br]', replacement: <br /> },
};

/**
 * Interpolates {fragment} into the given {haystack} and returns an array of
 * string parts and react fragments ready to assemble into a component
 *
 * But how?
 * given this string: `'[replace] me [replace] me [replace] me'` and told to
 * interpolate a <span></span> fragment wherever the needle '[replace]' is
 * found, we do this:
 *
 * `'[replace] me [replace] me [replace] me'` -> split into...
 * `['', ' me ', ' me ', ' me ', ' me ']`, -> intersperse fragment...
 * `['', <span></span>, ' me ', <span></span>, ' me ', <span></span>, ' me ', <span></span>, ' me ']`, -> shove in a component
 * then return that.
 *
 * The key wrangling and cloneElement is simply to differentiate duplicated
 * elements for react, otherwise it can't mount properly.
 *
 * @param {string} haystack the string to interpolate (e.g. "insert a [link] iin this")
 * @param {string|regex} needle The search term or regex eg "[link]"
 * @param {component} fragment The react component to intersperse
 */
const interpolateToArray = (haystack, needle, fragment, key = '') => {
  return intersperse(fragment, haystack.split(needle))
    .map((part, idx) => {
      if (equals(fragment, part)) {
        return React.cloneElement(part, { key: `${key}${idx}` });
      }
      return part;
    })
    .filter((part) => Boolean(part));
};

/**
 * Interpolates {fragment} into the given {haystack} and returns a react
 * fragment (for a "how?" see above!)
 *
 * @param {string} haystack the string to interpolate (e.g. "insert a [link] iin this")
 * @param {string|regex} needle The search term or regex eg "[link]"
 * @param {component} fragment The react component to intersperse
 */
export const interpolate = (haystack, needle, fragment) => (
  <>{interpolateToArray(haystack, needle, fragment)}</>
);

/**
 * given the following:
 * interpolateMany(
 *  'replace [this] and [that]',
 *  [
 *    ['this', <span>See this?</span>],
 *    ['that', <p>lorem ipsum...</p>]
 *  ]
 * )
 *
 * this function will return a react component like:
 *
 * <>replace <span>See this?</span> and <p>lorem ipsum...</p></>
 *
 * It does this by replacing the first search group, getting back an array of
 * components and strings, then replacing the subsequent search groups in each
 * string in the array.
 *
 * @param {string} haystack
 * @param {array<[needle, fragment]>} replacements each replacement should be an
 *                                    array of needle and fragment, where needle
 *                                    is the search term and fragment is the
 *                                    replacement
 */
export const interpolateMany = (haystack, replacements = []) => (
  <>
    {replacements.reduce(
      (parts, [needle, fragment], rIdx) => {
        return flatten(
          parts.map((part, partIdx) => {
            if (is(String, part)) {
              return interpolateToArray(
                part,
                needle,
                fragment,
                ((fragment && fragment.key) || '') + rIdx + partIdx,
              );
            }

            return part;
          }),
        );
      },
      [haystack],
    )}
  </>
);
